/**
 * Wire protocol — the contract between every driver and the result parser.
 *
 * This module is the definition of that contract: the results marker, the
 * exact key set and shapes of the payload a driver prints, and the presence
 * rules that say when each key is there. Language-independent.
 *
 * Before this module the contract existed only as three independently
 * maintained driver templates plus the parser's tolerant reader, kept in
 * lockstep by comments. A fourth language had three examples to imitate
 * instead of a spec.
 *
 * ## Shape of an execution
 *
 * A driver writes free-form user output (prints, logs), then the results
 * marker on its own line, then exactly one results envelope. Everything before
 * the marker is the student's output; everything after is ours.
 *
 * ## What this module covers, and what it does not
 *
 * The TOP-LEVEL keys of the four objects on the wire: the results envelope,
 * the
 * test-case row and operation spec the generator sends a driver, and the case
 * and operation results a driver sends back.
 *
 * It does NOT cover the VALUE-level vocabulary nested inside those keys — the
 * `{ type, data }` marker that `wrapSpecialTypeInputs` puts around a TreeNode
 * or ListNode input and every driver's `prepare_value` decodes, and the
 * `class-instance` wrapper name that goes with it. That vocabulary lives with
 * `CLASS_INSTANCE_WRAPPER` and is as much a cross-file spelling agreement as
 * anything here; it is named rather than duplicated so there is one home per
 * fact. A language author needs both declarations.
 *
 * The parser is the only intended reader of the results envelope. One other reader
 * exists today — `parseTestCasesFromStdout` in the admin approach editor,
 * which finds the marker itself and handles only the legacy bare-array
 * payload. That is a pre-existing duplicate, tracked as LG-771; do not add
 * another.
 *
 * ## The casing law
 *
 * **New wire keys are camelCase.** The snake_case set is CLOSED LEGACY —
 * `error_type`, `expected_inputs_after`, `expects_error`, `property_to_check`,
 * `expected_property_value`, `return_type`, `constructor_args`. Nothing may be
 * added to it. They are spelled out in {@link LEGACY_SNAKE_KEYS} so the closure
 * is enumerable rather than remembered, and each is marked at its use site
 * below.
 *
 * The closure is enforced in both directions, which is what makes it a closure
 * rather than a wish: {@link _CasingLawHolds} fails the BUILD on a declared
 * snake_case key outside the set, and the driver tests fail on a snake_case key
 * a driver EMITS without declaring — the direction that would otherwise let the
 * wire drift past the declaration unnoticed.
 *
 * The mixed casing is why the input side reads `expected_inputs_after` while
 * the result side reads `inputsAfter` for the same concept, and why a result
 * object can carry `failure.errorType` next to a legacy sibling `error_type`.
 * Both spellings stay on the wire; `normalizeTestResults` is the single point
 * that folds legacy into the typed shape.
 *
 * ## Which keys the drivers interpolate
 *
 * A driver template references a constant from here exactly when the wire
 * spelling DIFFERS from the name the TypeScript side uses for the same value
 * — `tc.expectedInputsAfter` becoming `expected_inputs_after`, `op.expectsError`
 * becoming `expects_error`, `errorType` becoming `error_type`. That predicate
 * selects the marker and the seven closed-legacy keys, and nothing else: for
 * every other key the wire spelling and the TypeScript name are the same word,
 * so there is no second spelling to keep in sync and interpolating would only
 * make the generated source harder to read.
 *
 * Apply the predicate to one key at a time; it is not a list to memorize. A
 * new camelCase key (LG-669's `rawReturn`) has one spelling on both sides, so
 * it stays a plain literal in the drivers.
 *
 * Little is lost by that: the driver-correspondence test asserts each driver's
 * generated source spells every key declared here IN AN EMITTING POSITION —
 * an assignment or a literal key, never a read of the generator's own
 * vocabulary — so a hand-written literal that drifts from this file fails that
 * test whether or not it was interpolated.
 *
 * What that test does NOT check is which object a key lands on: `expected` is
 * emitted on both the case row and the operation row, and one of the two going
 * missing leaves the other to satisfy the probe. Pinning the runtime shape is
 * the golden suites' job, and they assert only on the fields they read. So the
 * literal-vs-constant choice is a readability call for the SPELLING of a key,
 * and not a licence to assume every emitted key is covered end to end.
 *
 * ## Behavior
 *
 * Declaring the protocol changes nothing about it. These are names and types,
 * never runtime validation: a payload that parses today must still parse.
 * Persisted `CodeExecution.testResults` rows predate this module and stay
 * readable through the same `normalizeTestResults` upgrade path.
 */

import type { TestFailure } from '../types/test-cases.js'

// =============================================================================
// Results marker
// =============================================================================

/**
 * Separates user output from the results envelope. The driver prints it on its
 * own line, then the JSON envelope.
 *
 * The parser splits on the FIRST occurrence and treats everything before it as
 * user output. That choice predates this module and is NOT merely a shifted
 * split when user code prints the marker too — both consequences are real and
 * reproduced against live drivers:
 *
 * - A solution that prints this string anywhere (a debug dump, an echoed
 *   input) makes the payload `<their text> … <marker> … <real envelope>`, which
 *   fails `JSON.parse`. The valid envelope sitting later in the same stdout is
 *   discarded and the student is shown a harness error for a correct run.
 * - A solution that prints the marker and a well-formed envelope and then exits
 *   before the driver emits (`os._exit`, `process.exit`, `System.exit`) leaves
 *   its own as the only marker in stdout. The parser reports `success: true`
 *   with every case passed, on the graded Submit path.
 *
 * Picking a different marker string does not fix either one: the exposure is in
 * reading the FIRST occurrence, not in the string. The fix is to anchor the
 * marker to a whole line and take the LAST one (the envelope is always a single
 * line, so the driver's is always last), which closes the first case outright;
 * the second additionally needs a per-execution nonce the driver interpolates
 * and the parser requires. Both halves are tracked by LG-785; until it lands,
 * treat a self-reported pass as possible.
 */
export const RESULTS_MARKER = '===BCTCI_RESULTS==='

// =============================================================================
// Key names
// =============================================================================

/**
 * The closed legacy snake_case set. Nothing is ever added here — new keys are
 * camelCase (see the casing law above). Listed for enumerability; the use
 * sites below are what the generator and drivers actually reference.
 */
export const LEGACY_SNAKE_KEYS = [
  'error_type',
  'expected_inputs_after',
  'expects_error',
  'property_to_check',
  'expected_property_value',
  'return_type',
  'constructor_args',
] as const

export type LegacySnakeKey = (typeof LEGACY_SNAKE_KEYS)[number]

/**
 * The top-level keys of each object on the wire. Property names here are
 * camelCase TypeScript identifiers; the VALUES are the wire spellings and are
 * the only thing that matters to a driver.
 *
 * Each group is `satisfies Record<string, keyof …>` against the interface that
 * describes the same object, so the two halves of this module cannot drift:
 * renaming a field on `WireCaseResult` without renaming its entry here is a
 * compile error, which is the failure mode this whole ticket exists to end.
 */
export const WIRE_KEYS = {
  /**
   * Results envelope — the single JSON object (or, in raw mode and legacy
   * stored output, array) printed after the results marker. See
   * {@link WireEnvelope}.
   */
  envelope: {
    /** Per-case results. Present on a completed run. */
    results: 'results',
    /**
     * Suite size, NOT the number of executed cases: a Submit run stops at the
     * first failure, so `results.length <= total`. Cases that never ran count
     * as failed.
     */
    total: 'total',
    /**
     * Program-level failure — a syntax error or import-time crash in the
     * student's code, reported THROUGH the protocol rather than as a bare
     * stderr trace. Mutually exclusive with `results`.
     */
    programFailure: 'programFailure',
    // WireEnvelopeKey, not `keyof` of the intersection: the intersection
    // REDUCES TO NEVER now that each envelope variant bans the other's key
    // with a `?: never` marker — same trap as the testCase group below.
  } satisfies Record<string, WireEnvelopeKey>,

  /**
   * Test-case row (generator → driver). One per case in the driver's
   * TEST_CASES array. Function-based and class-based cases share the
   * authoring-context keys and differ in everything else — the presence rules
   * are the {@link WireFunctionTestCase} / {@link WireClassTestCase} variants.
   */
  testCase: {
    /** Function cases: param name → value, in declared `inputParameters` order. */
    inputs: 'inputs',
    /** Function cases: the expected return value. */
    expected: 'expected',
    /**
     * Function cases, in-place problems only: param name → expected post-call
     * state. Its presence switches the runner from return-value comparison to
     * post-state comparison. LEGACY SNAKE — the result-side name for the same
     * concept is `inputsAfter`.
     */
    expectedInputsAfter: 'expected_inputs_after',
    /**
     * Function cases: the adjacency input echoed for path-validating
     * comparators. Present only when a declared param is `integer[][]`/`graph`.
     */
    graph: 'graph',
    /** Class cases: positional constructor args. LEGACY SNAKE. */
    constructorArgs: 'constructor_args',
    /** Class cases: the operation sequence. See {@link WIRE_KEYS.operationSpec}. */
    operations: 'operations',
    /** Authoring context, echoed back onto the result. Present when authored. */
    intent: 'intent',
    inputDisplayOverride: 'inputDisplayOverride',
    outputDisplayOverride: 'outputDisplayOverride',
    // WireTestCaseKey, not `keyof` of the union or the intersection: the
    // union's keyof is the INTERSECTION of the variants' keys (see
    // AllWireSpellings below), rejecting every variant-specific entry, and
    // the intersection REDUCES TO NEVER now that each variant bans the
    // other's keys with `?: never` markers.
  } satisfies Record<string, WireTestCaseKey>,

  /**
   * Operation spec (generator → driver), one row of a class case's
   * `operations`. Comments and malformed rows are dropped by the generator, so
   * a driver only ever sees well-formed operations.
   */
  operationSpec: {
    /** Method name on the instance under test. */
    method: 'method',
    /** Positional args, marker-wrapped for node/class-instance types. */
    args: 'args',
    /** Expected return value. */
    expected: 'expected',
    /** When true the operation must raise; a normal return fails it. LEGACY SNAKE. */
    expectsError: 'expects_error',
    /**
     * Instance attribute to read INSTEAD of the return value (in-place
     * operations). Null when unused. LEGACY SNAKE.
     */
    propertyToCheck: 'property_to_check',
    /** Expected value of `property_to_check`. Null when unused. LEGACY SNAKE. */
    expectedPropertyValue: 'expected_property_value',
    /**
     * Declared return type from the method signature, or null on a legacy
     * untyped config. `"void"` means the return value is ignored (LeetCode
     * semantics) and both expected/actual are nulled on the result row.
     * LEGACY SNAKE.
     */
    returnType: 'return_type',
  } satisfies Record<string, keyof WireOperationSpec>,

  /** Per-case result (driver → parser). One per EXECUTED case. */
  caseResult: {
    /** 0-based index into the suite. Stable under stop-early. */
    index: 'index',
    passed: 'passed',
    /** Function cases: expected/actual return value, or — on an in-place case — the post-state of the displayed param. */
    expected: 'expected',
    actual: 'actual',
    /**
     * The case's inputs, echoed. Emitted by the FUNCTION runners on every
     * executed result, passing included, so the Run view can render full
     * per-case tabs; absent on legacy stored results and on cases stop-early
     * never reached.
     *
     * The CLASS runners never emit it: a class case's inputs are its
     * constructor args and its operation sequence, which reach the UI as
     * `operations` rows instead.
     */
    inputs: 'inputs',
    /**
     * Post-call state of every input param, serialized. Emitted by the FUNCTION
     * runners on a FAILING custom-verify case only (LG-561): those verifiers
     * return a bare boolean, so one that rejects on post-call state renders an
     * otherwise identical expected/actual with no reason. A passing case has
     * nothing to explain and carries none.
     *
     * The CLASS runners never emit this — there custom-verify receives the
     * object under test rather than the input map, so the analogous evidence is
     * {@link WIRE_KEYS.operationResult.rawReturn} on the operation row.
     */
    inputsAfter: 'inputsAfter',
    /** Class cases: per-operation rows. See {@link WIRE_KEYS.operationResult}. */
    operations: 'operations',
    /** Structured failure. Present only on a failing result. */
    failure: 'failure',
    /** Authoring context, echoed from the test-case row when authored. */
    intent: 'intent',
    inputDisplayOverride: 'inputDisplayOverride',
    outputDisplayOverride: 'outputDisplayOverride',
    /**
     * @deprecated Legacy string error — read `failure.message`. Still emitted
     * beside `failure` on a runtime error until the last reader is gone.
     */
    error: 'error',
    /**
     * @deprecated Legacy error class name — read `failure.errorType`. LEGACY
     * SNAKE, and the reason `normalizeTestResults` exists: it is folded into
     * `errorType` and stripped at the single normalization point.
     */
    errorType: 'error_type',
  } satisfies Record<string, keyof WireCaseResult>,

  /**
   * Per-operation result (driver → parser), inside a class case's
   * `operations`. A case ALWAYS stops at its first failing operation — later
   * operations run against corrupt state and mislead — so the rows end at the
   * first failure.
   */
  operationResult: {
    passed: 'passed',
    method: 'method',
    args: 'args',
    expected: 'expected',
    actual: 'actual',
    /** Structured failure. Present only on a failing operation. */
    failure: 'failure',
    /**
     * Raw serialized return value of the operation, as evidence (LG-669).
     *
     * PRESENCE RULE: failing custom-verify operation rows only. A class
     * custom-verify returns a bare boolean, so an operation it rejects can
     * render identical expected/actual — or, on a declared-`void` method, two
     * empty cells, since both sides are nulled by design. This carries what the
     * method actually returned, which is exactly the fact those cells hide.
     *
     * DECLARED HERE, NOT EMITTED. LG-672 names the key; LG-669 adds the
     * emission in all three class runners, the async-submit passthrough, and
     * the UI. Declaring it early fixes the name before three separate call
     * sites need to agree on it. Note that LG-669 must ALSO add the field to
     * `OperationTestResult` — it rides through
     * `normalizeTestResults` untouched at runtime, but no consumer can read it
     * until the typed result declares it too.
     *
     * Serialization is per-field and non-fatal: a value the serializer chokes
     * on is dropped from the row rather than rewriting a decided wrong answer
     * into a runtime error. Display data never changes a verdict.
     */
    rawReturn: 'rawReturn',
    /** @deprecated Legacy string error — read `failure.message`. */
    error: 'error',
    /** @deprecated Legacy error class name — read `failure.errorType`. LEGACY SNAKE. */
    errorType: 'error_type',
  } satisfies Record<string, keyof WireOperationResult>,

  /**
   * Structured failure payload (LG-165), on both `programFailure` and the
   * `failure` key of a case or operation result. Already camelCase throughout;
   * the shape is {@link TestFailure}.
   */
  failure: {
    /** `'wrong_answer'` | `'runtime_error'`. */
    kind: 'kind',
    /** Exception class name. `runtime_error` only. */
    errorType: 'errorType',
    message: 'message',
    /**
     * Frames from the user's code only, editor-relative (the driver has
     * already subtracted any generated preamble), innermost last. An empty
     * array means a frameless error and the UI makes no line claim.
     */
    userFrames: 'userFrames',
  } satisfies Record<string, keyof TestFailure>,

  /**
   * Raw-mode result. Raw programs run admin-authored scripts rather than a
   * generated runner, and emit a bare array of these — no envelope, no
   * per-case metadata.
   */
  rawResult: {
    passed: 'passed',
    /** Formatted traceback of the throw that failed the script. */
    error: 'error',
  } satisfies Record<string, keyof WireRawResult>,
} as const

// =============================================================================
// Payload shapes — generator → driver
// =============================================================================

/**
 * Authoring context shared by both test-case shapes, echoed back onto the
 * result row when authored.
 */
interface WireTestCaseContext {
  intent?: string
  inputDisplayOverride?: string
  outputDisplayOverride?: string
}

/**
 * One row of a FUNCTION driver's TEST_CASES array. The generator's row
 * variables are annotated with this type, so a missing required key, a
 * misspelled key, or a class-side key on a function row fails the build.
 */
export interface WireFunctionTestCase extends WireTestCaseContext {
  /** Param name → value, in declared `inputParameters` order. Always present. */
  inputs: Record<string, unknown>
  /** Expected return value. Always present; `null` when the author left it blank. */
  expected: unknown
  /**
   * In-place problems only: param name → expected post-call state. Its
   * presence switches the runner from return-value comparison to post-state
   * comparison. LEGACY SNAKE.
   */
  expected_inputs_after?: Record<string, unknown>
  /** Path-validating comparators only: the adjacency input, echoed. */
  graph?: unknown
  /**
   * Exclusivity markers, not wire keys: the class variant's keys are banned
   * here so a hybrid row is rejected even through the {@link WireTestCase}
   * union, which would otherwise accept any literal covering either variant.
   * `?: never` still admits an EXPLICIT `undefined` — closing that needs the
   * repo-wide `exactOptionalPropertyTypes` flag — but `JSON.stringify` drops
   * `undefined`-valued keys, so that corner cannot reach the wire.
   */
  constructor_args?: never
  operations?: never
}

/** One row of a CLASS driver's TEST_CASES array. Checked like the function row. */
export interface WireClassTestCase extends WireTestCaseContext {
  /** Positional constructor args. Always present. LEGACY SNAKE. */
  constructor_args: unknown[]
  /** The operation sequence. Always present. */
  operations: WireOperationSpec[]
  /** Exclusivity markers — see the function variant's note. */
  inputs?: never
  expected?: never
  expected_inputs_after?: never
  graph?: never
}

/**
 * One row of the driver's TEST_CASES array — a suite is all-function or
 * all-class, decided by the config's `testType`, never mixed. The exclusivity
 * markers on the variants make a hybrid literal a compile error even through
 * this union.
 */
export type WireTestCase = WireFunctionTestCase | WireClassTestCase

/**
 * Every key a test-case row can carry, across both variants. This is the
 * spelling census the guards use — never `keyof` the union (the intersection
 * of the variants' keys) or `keyof` the intersection (`never`, because each
 * variant bans the other's keys).
 */
type WireTestCaseKey = keyof WireFunctionTestCase | keyof WireClassTestCase

/** One row of a class case's `operations`, as the generator sends it. */
export interface WireOperationSpec {
  method: string
  args: unknown[]
  /**
   * Optional because the generator forwards them as-is: an `undefined` value
   * makes `JSON.stringify` drop the key, so these two really can be absent on
   * the wire. The three below cannot — the generator coalesces each to `null`,
   * and `null` survives serialization.
   */
  expected?: unknown
  expects_error?: boolean
  /** Null when unused. Always present. */
  property_to_check: string | null
  /** Null when unused. Always present. */
  expected_property_value: unknown
  /** Null on a legacy untyped config. Always present. */
  return_type: string | null
}

// =============================================================================
// Payload shapes — driver → parser
// =============================================================================
//
// These describe the payload AS IT ARRIVES — before `normalizeTestResults`
// folds the legacy keys away. The post-normalization shapes a consumer sees
// are `TestResult` / `OperationTestResult`; the difference
// between the two families is exactly what normalize does.

/** One row of a class case's `operations`, as emitted. */
export interface WireOperationResult {
  passed: boolean
  /**
   * The operation's identity, echoed from its {@link WireOperationSpec}.
   * Every current class runner emits `method` and `args` on every row —
   * success, expected-error, and exception paths alike — and a new runner
   * must too. They are optional here only because this shape also describes
   * persisted rows that the async submit path still re-parses, and rows from
   * before operation-level tracking predate both keys (the same legacy
   * carve-out `OperationTestResult` documents post-normalization).
   */
  method?: string
  /** See `method` — always emitted; absent only on pre-tracking legacy rows. */
  args?: unknown[]
  expected?: unknown
  actual?: unknown
  failure?: TestFailure
  /** LG-669 evidence; failing custom-verify rows only. Not yet emitted. */
  rawReturn?: unknown
  /** @deprecated legacy string error */
  error?: string
  /** @deprecated legacy error class name, folded into `failure.errorType` */
  error_type?: string
}

/** One executed test case, as emitted. */
export interface WireCaseResult {
  index: number
  passed: boolean
  expected?: unknown
  actual?: unknown
  /** Function runners only. */
  inputs?: Record<string, unknown>
  /** Function runners, failing custom-verify cases only. */
  inputsAfter?: Record<string, unknown>
  /** Class cases only. */
  operations?: WireOperationResult[]
  /** Failing results only. */
  failure?: TestFailure
  intent?: string
  inputDisplayOverride?: string
  outputDisplayOverride?: string
  /** @deprecated legacy string error */
  error?: string
  /** @deprecated legacy error class name, folded into `errorType` by normalize */
  error_type?: string
}

/** A completed run: every executed case, plus the full suite size. */
export interface WireResultsEnvelope {
  results: WireCaseResult[]
  total: number
  /**
   * Exclusivity marker, not a wire key: the failure variant's key is banned
   * here so a hybrid envelope carrying both a completed run and a program
   * failure is rejected even through the {@link WireEnvelope} union. Same
   * construction and explicit-`undefined` caveat as
   * {@link WireFunctionTestCase}'s markers.
   */
  programFailure?: never
}

/**
 * The program never got as far as running cases — a syntax error or an
 * import-time crash in the student's code. `total` is still the suite size, so
 * the summary can report every case as failed.
 */
export interface WireProgramFailureEnvelope {
  programFailure: TestFailure
  total: number
  /** Exclusivity marker — see {@link WireResultsEnvelope}'s note. */
  results?: never
}

/**
 * Every key an envelope object can carry, across both variants — the spelling
 * census the guards use, for the same never-reduction reason as
 * {@link WireTestCase}'s: each variant bans the other's key, so `keyof` of the
 * intersection collapses.
 */
type WireEnvelopeKey =
  | keyof WireResultsEnvelope
  | keyof WireProgramFailureEnvelope

/** One script's outcome in raw mode. */
export interface WireRawResult {
  passed: boolean
  error?: string
}

// =============================================================================
// Completeness
// =============================================================================
//
// `satisfies Record<string, keyof Wire…>` on each WIRE_KEYS group above guards
// one direction: an entry that no longer names a real field is a compile
// error. These guard the other. Adding a field to an interface and forgetting
// its WIRE_KEYS entry would otherwise compile silently — and no runtime test
// can catch it, because `keyof` does not survive to runtime — leaving the
// census quietly incomplete for the next language author who reads it as the
// spec.

/** The wire spellings a WIRE_KEYS group declares. */
type Declared<G> = G[keyof G]

/**
 * `never` when every field of `Shape` has a WIRE_KEYS entry. Otherwise it is
 * the missing key(s), and the assignment below fails with that key in the
 * error message.
 */
type Undeclared<Shape, G> = Exclude<keyof Shape, Declared<G>>

type AssertNone<T extends never> = T

export type _EnvelopeComplete = AssertNone<
  // WireEnvelopeKey for the same never-reduction reason as _TestCaseComplete.
  Exclude<WireEnvelopeKey, Declared<typeof WIRE_KEYS.envelope>>
>
export type _TestCaseComplete = AssertNone<
  // WireTestCaseKey for the same never-reduction reason as the satisfies
  // above — with the exclusivity markers, each variant carries every key, so
  // the union of the two keyofs is the full census.
  Exclude<WireTestCaseKey, Declared<typeof WIRE_KEYS.testCase>>
>

/**
 * The exclusivity markers are a hand-maintained complement — each variant
 * lists the other's keys as `?: never` — so this pins the two key sets equal.
 * Without it, adding a key to one variant without the marker on the other
 * silently reopens the hybrid hole; with it, the build fails naming the
 * unmirrored key. It pins key SETS, not marker TYPES: retyping a marker
 * (`graph?: never` → `graph?: unknown`) passes it, so the markers' own doc
 * comment is the only guard against that deliberate an edit.
 */
export type _ExclusivityComplete = AssertNone<
  | Exclude<keyof WireFunctionTestCase, keyof WireClassTestCase>
  | Exclude<keyof WireClassTestCase, keyof WireFunctionTestCase>
>
/** The envelope variants' markers, pinned the same way. */
export type _EnvelopeExclusivityComplete = AssertNone<
  | Exclude<keyof WireResultsEnvelope, keyof WireProgramFailureEnvelope>
  | Exclude<keyof WireProgramFailureEnvelope, keyof WireResultsEnvelope>
>
export type _OperationSpecComplete = AssertNone<
  Undeclared<WireOperationSpec, typeof WIRE_KEYS.operationSpec>
>
export type _CaseResultComplete = AssertNone<
  Undeclared<WireCaseResult, typeof WIRE_KEYS.caseResult>
>
export type _OperationResultComplete = AssertNone<
  Undeclared<WireOperationResult, typeof WIRE_KEYS.operationResult>
>
export type _FailureComplete = AssertNone<
  Undeclared<TestFailure, typeof WIRE_KEYS.failure>
>
export type _RawResultComplete = AssertNone<
  Undeclared<WireRawResult, typeof WIRE_KEYS.rawResult>
>

/**
 * Every wire spelling in every group.
 *
 * Written as a mapped type indexed by `keyof`, NOT as `Declared<WIRE_KEYS[keyof
 * WIRE_KEYS]>`: indexing a UNION of the group objects would ask for `keyof` of
 * that union, which is the INTERSECTION of their keys — empty here — collapsing
 * the whole thing to `never` and passing the check below vacuously.
 */
type AllWireSpellings = {
  [G in keyof typeof WIRE_KEYS]: Declared<(typeof WIRE_KEYS)[G]>
}[keyof typeof WIRE_KEYS]

/**
 * The casing law, enforced by the compiler rather than by a test run: a new
 * snake_case wire key that is not in the closed legacy set fails here, naming
 * the offending spelling. This is what makes {@link LegacySnakeKey}
 * load-bearing instead of decorative.
 */
export type _CasingLawHolds = AssertNone<
  Exclude<Extract<AllWireSpellings, `${string}_${string}`>, LegacySnakeKey>
>

/**
 * What a driver prints after the results marker.
 *
 * The two OBJECT branches are mutually exclusive by construction: each bans
 * the other's discriminating key with a `?: never` marker, so a literal
 * carrying both `results` and `programFailure` is a compile error rather than
 * silently matching one variant.
 *
 * The two array branches are distinct payloads that happen to share a shape
 * on the outside: `WireRawResult[]` is what raw-mode programs emit today,
 * and `WireCaseResult[]` is the pre-envelope shape of stored stdout that
 * `execution.submitAsync` still re-parses. Both must stay readable.
 *
 * NOTHING TELLS THEM APART FROM THE PAYLOAD. The caller picks the reader from
 * its own `testType` — `TEST_TYPE.RAW` selects `parseRawTestOutput`, everything
 * else `parseTestOutput` — see `parseExecutionOutcome` and the
 * `execution.submitAsync` path. Neither
 * reader probes the rows: `parseTestOutput` normalizes whatever array it is
 * handed, and `parseRawTestOutput` overwrites `index` with its own loop
 * counter. So a reader that unified the two paths, or dropped the `TEST_TYPE`
 * branch, would report raw output as ordinary case results with fabricated
 * indices and raise nothing. Keep the discriminator where it is.
 */
export type WireEnvelope =
  | WireResultsEnvelope
  | WireProgramFailureEnvelope
  | WireCaseResult[]
  | WireRawResult[]
